import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../utils/api";
import dayjs from "dayjs";
import MainCard from "components/MainCard";
import PageHeader from "components/PageHeader";
import { useCurrency } from "contexts/CurrencyContext";
import { formatCurrency } from "utils/currency";

import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import ScrollableTableContainer from "components/table/ScrollableTableContainer";
import TableActionButton from "components/table/TableActionButton";
import TableActions from "components/table/TableActions";
import { couponCodeChipSx, offerStatusChipSx, offerTypeChipSx } from "components/table/tableChipStyles";
import brand from "utils/designTokens";

import {
  Box, Grid, Button, Typography, Chip, IconButton, TextField, MenuItem,
  Table, TableBody, TableCell, TableHead, TableRow,
  TablePagination, Paper, Tooltip, Switch,
  CircularProgress, Alert, Stack, LinearProgress
} from "@mui/material";

export const OFFER_TYPES = [
  { value: "discount", label: "Discount" },
  { value: "coupon", label: "Coupon" },
  { value: "category", label: "Category Offer" },
  { value: "item", label: "Item Offer" },
  { value: "store", label: "Store Offer" },
  { value: "customer", label: "Customer Offer" },
  { value: "order_count", label: "Order Count" }
];

export const getOfferType = (value) =>
  OFFER_TYPES.find((t) => t.value === value) || OFFER_TYPES[0];

const STATUS_LABELS = {
  active: "Active",
  inactive: "Inactive",
  expired: "Expired",
  upcoming: "Upcoming"
};

const getOfferStatus = (offer) => {
  const now  = dayjs();
  const from = dayjs(offer.from_datetime);
  const to   = dayjs(offer.to_datetime);
  if (!offer.status) return "inactive";
  if (now.isBefore(from)) return "upcoming";
  if (now.isAfter(to))    return "expired";
  return "active";
};

const StatCard = ({ label, value }) => (
  <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
    <Typography variant="h5" fontWeight={700} color="primary.main">{value}</Typography>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
  </Paper>
);

export default function OfferList() {
  const { currency } = useCurrency();
  const navigate = useNavigate();
  const [offers,       setOffers]       = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState("");
  const [page,         setPage]         = useState(0);
  const [rowsPerPage,  setRowsPerPage]  = useState(10);
  const [search,       setSearch]       = useState("");
  const [typeFilter,   setTypeFilter]   = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const loadOffers = useCallback(async () => {
    setLoading(true);
    try {
    
    const res = await api.get("/offer/list");
setOffers(Array.isArray(res.data?.data?.offers) ? res.data.data.offers : []);
    } catch {
      setError("Failed to load offers");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadOffers(); }, [loadOffers]);

  const [updatingId, setUpdatingId] = useState(null);

  const handleToggleStatus = async (id, current) => {
    setUpdatingId(id);
    setError("");
    try {
     
      await api.patch(`/offers/manage/${id}`, { status: !current });
      
      const res = await api.get(`/offer/list/${id}`);
      const updatedOffer = res.data?.data?.offer || res.data?.data;
      
      if (updatedOffer) {
        setOffers((prev) =>
          prev.map((o) => ((o.id === id || o.offer_id === id) ? { ...o, ...updatedOffer } : o))
        );
      } else {
       
        setOffers((prev) =>
          prev.map((o) => ((o.id === id || o.offer_id === id) ? { ...o, status: !current } : o))
        );
      }
    } catch (err) {
      setError(err.response?.data?.message || "Status update failed");
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this offer?")) return;
    try {
     
      await api.delete(`/offers/delete/${id}`);
      setOffers((prev) => prev.filter((o) => (o.id !== id && o.offer_id !== id)));
    } catch {
      setError("Delete failed");
    }
  };

  const filtered = offers.filter((o) => {
    const matchSearch =
      o.offer_name_en?.toLowerCase().includes(search.toLowerCase()) ||
      o.offer_name_ar?.includes(search) ||
      o.coupon_code?.toLowerCase().includes(search.toLowerCase());
    const matchType   = typeFilter   === "all" || o.offer_type  === typeFilter;
    const matchStatus = statusFilter === "all" || getOfferStatus(o) === statusFilter;
    return matchSearch && matchType && matchStatus;
  });

  const paginated = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const stats = {
    total:   offers.length,
    active:  offers.filter((o) => getOfferStatus(o) === "active").length,
    expired: offers.filter((o) => getOfferStatus(o) === "expired").length,
    upcoming:offers.filter((o) => getOfferStatus(o) === "upcoming").length,
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <PageHeader
        title="Offers"
        subtitle="Manage discounts, coupons, and promotional offers."
        count={filtered.length}
        actionLabel="Create Offer"
        actionIcon={AddIcon}
        onAction={() => navigate("/offer/create")}
      />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: "Total Offers", value: stats.total },
          { label: "Active", value: stats.active },
          { label: "Upcoming", value: stats.upcoming },
          { label: "Expired", value: stats.expired }
        ].map((s) => (
          <Grid size={{ xs: 6, md: 3 }} key={s.label}>
            <StatCard {...s} />
          </Grid>
        ))}
      </Grid>

      <MainCard
        contentSX={{ p: 0 }}
        sx={{ borderRadius: 3, position: "relative" }}
      >
        {(loading || updatingId) && (
          <LinearProgress sx={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 10, borderTopLeftRadius: 12, borderTopRightRadius: 12 }} />
        )}
        {/* Filters */}
        <Box sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider", display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
          <TextField
            size="small" placeholder="Search offers…"
            value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary", fontSize: 18 }} /> }}
            sx={{ minWidth: 240 }}
          />
          <TextField
            select size="small" label="Type" value={typeFilter}
            onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="all">All Types</MenuItem>
            {OFFER_TYPES.map((t) => (
              <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            select size="small" label="Status" value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
            sx={{ minWidth: 140 }}
          >
            {["all", "active", "upcoming", "expired", "inactive"].map((s) => (
              <MenuItem key={s} value={s}>{s === "all" ? "All Status" : STATUS_LABELS[s] || s}</MenuItem>
            ))}
          </TextField>
          <Box sx={{ flex: 1 }} />
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <ScrollableTableContainer sx={{ maxHeight: 'calc(100vh - 320px)' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ "& th": { fontWeight: 700, bgcolor: "grey.50", whiteSpace: "nowrap" } }}>
                    <TableCell>Offer</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Discount</TableCell>
                    <TableCell>Coupon Code</TableCell>
                    <TableCell>Validity</TableCell>
                    <TableCell>Min. Purchase</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Active</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginated.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 6, color: "text.secondary" }}>
                        No offers found
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginated.map((offer) => {
                      const id     = offer.id || offer.offer_id;
                      const ot     = getOfferType(offer.offer_type);
                      const status = getOfferStatus(offer);
                      const statusLabel = STATUS_LABELS[status] || status;
                      return (
                        <TableRow key={id} hover sx={{ "& td": { py: 1.5 } }}>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" fontWeight={600}>{offer.offer_name_en}</Typography>
                              <Typography variant="caption" color="text.secondary">{offer.offer_name_ar}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip label={ot.label} size="small" variant="outlined" sx={offerTypeChipSx} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={700} sx={{ color: brand.primary }}>
                              {offer.offer_value_type === "percentage" ? `${offer.offer_value}%` : formatCurrency(offer.offer_value, currency.code, currency.symbol)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {offer.coupon_code
                              ? <Chip label={offer.coupon_code} size="small" variant="outlined" sx={couponCodeChipSx} />
                              : "—"}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            <Typography variant="caption">
                              {dayjs(offer.from_datetime).format("DD MMM YY, hh:mm A")}
                            </Typography>
                            <br />
                            <Typography variant="caption" color="text.secondary">
                              → {dayjs(offer.to_datetime).format("DD MMM YY, hh:mm A")}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {offer.minimum_purchase_amount ? formatCurrency(offer.minimum_purchase_amount, currency.code, currency.symbol) : "—"}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={statusLabel}
                              size="small"
                              sx={offerStatusChipSx(status)}
                            />
                          </TableCell>
                          <TableCell align="center">
                            {updatingId === id ? (
                              <CircularProgress size={20} sx={{ color: brand.primary }} />
                            ) : (
                              <Switch
                                size="small"
                                checked={!!offer.status}
                                onChange={() => handleToggleStatus(id, offer.status)}
                                sx={{
                                  '& .MuiSwitch-switchBase.Mui-checked': {
                                    color: brand.primary,
                                    '&:hover': { bgcolor: 'rgba(31, 91, 64, 0.08)' }
                                  },
                                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                    bgcolor: brand.primary
                                  }
                                }}
                              />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <TableActions>
                              <TableActionButton
                                action="edit"
                                onClick={() => navigate(`/offer/edit/${id}`)}
                              />
                              <TableActionButton
                                action="delete"
                                onClick={() => handleDelete(id)}
                              />
                            </TableActions>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </ScrollableTableContainer>
            <TablePagination
              component="div"
              count={filtered.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(+e.target.value); setPage(0); }}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </>
        )}
      </MainCard>
    </Box>
  );
}